import type { Components } from '@/api/generated/api';
import { ProductFeature, SubscriptionStatus } from '@/api/generated/api.enum';
import { computed, type ComputedRef } from 'vue';
import { daysUntil } from '@/utils/date.utils';
import {
  canCreateBillingOfferFromStatus,
  getProductMeta,
  hasSeatsAvailableForBillingStatus,
  isPurchasableProduct,
  type BillingInterval,
} from '@/utils/billing';
import { usePermissions } from '../usePermissions';
import { useBillingStatus } from './useBillingStatus';

export enum UIBillingState {
  Active = 'active',
  TrialEnding = 'trialEnding',
  TrialEnded = 'trialEnded',
  SubEnding = 'subEnding',
  SubEnded = 'subEnded',
}

const ALL_PRODUCT_FEATURES = Object.values(ProductFeature);
const ENDING_SOON_DAYS = 7;

type BillingStatus = ComputedRef<Components.Schemas.BillingStatusDto | undefined>;

function getFeatureAccess(billingStatus: BillingStatus) {
  const enabledFeatures = computed<Set<Components.Schemas.ProductFeature>>(() => {
    const status = billingStatus.value;
    if (!status) return new Set(ALL_PRODUCT_FEATURES);

    if (!status.isBillingActive) return new Set(ALL_PRODUCT_FEATURES);

    // Trials include every feature.
    if (status.isTrialActive) return new Set(ALL_PRODUCT_FEATURES);

    // Active purchased products determine access after the trial.
    const prods = status.products.filter((p) => p.isCurrentlyActive);
    return new Set<Components.Schemas.ProductFeature>(prods.flatMap((p) => p.enabledFeatures));
  });

  const hasFeature = (feature: Components.Schemas.ProductFeature): boolean => {
    return enabledFeatures.value.has(feature);
  };

  const hasSeatsAvailable = (seatType: Components.Schemas.SeatType): boolean => {
    return hasSeatsAvailableForBillingStatus(billingStatus.value, seatType);
  };

  return { hasFeature, hasSeatsAvailable };
}

function getPlanState(billingStatus: BillingStatus) {
  const allActiveProductTypeIds = computed(() => {
    const set = new Set<number>();
    const fb = billingStatus.value;
    if (!fb) return set;

    for (const p of fb.products.filter((product) => product.isCurrentlyActive)) set.add(p.productTypeId);

    return set;
  });

  const isPlanActive = (planId: number) => allActiveProductTypeIds.value.has(planId);

  const hasActivePlan = computed<boolean>(() => {
    const status = billingStatus.value;
    if (!status) return false;
    return status.hasActivePlan;
  });

  const isFreePlanOrTrial = computed<boolean>(() => {
    const status = billingStatus.value;
    if (!status) return false;
    return status.isTrialActive || !hasActivePlan.value;
  });

  return { isPlanActive, hasActivePlan, isFreePlanOrTrial };
}

function getSubscriptionState(billingStatus: BillingStatus) {
  const isEndedStatus = (status: Components.Schemas.SubscriptionStatus): boolean => {
    return (
      status === SubscriptionStatus.canceled ||
      status === SubscriptionStatus.unpaid ||
      status === SubscriptionStatus.incomplete ||
      status === SubscriptionStatus.incomplete_expired
    );
  };

  const isInvoicePastDue = computed<boolean>(() => {
    return billingStatus.value?.isInvoicePastDue ?? false;
  });

  const isSubEnding = computed<boolean>(() => {
    const s = billingStatus.value;
    if (!s) return false;
    const ends = daysUntil(s.currentPeriodEnd);
    return (
      s.hasActivePlan && !!s.cancelAtPeriodEnd && typeof ends === 'number' && ends >= 0 && ends <= ENDING_SOON_DAYS
    );
  });

  const isSubEnded = computed<boolean>(() => {
    const s = billingStatus.value;
    if (!s) return false;

    const endedByStatus = isEndedStatus(s.status);
    const endedBySchedule =
      !!s.cancelAtPeriodEnd &&
      !s.hasActivePlan &&
      typeof daysUntil(s.currentPeriodEnd) === 'number' &&
      (daysUntil(s.currentPeriodEnd) as number) < 0;

    return s.hasAnySubscriptionHistory && (endedByStatus || endedBySchedule || isInvoicePastDue.value);
  });

  return { isSubEnding, isSubEnded, isInvoicePastDue };
}

function getTrialState(billingStatus: BillingStatus, hasActivePlan: ComputedRef<boolean>) {
  const isTrialEnding = computed<boolean>(() => {
    const s = billingStatus.value;
    if (!s) return false;
    const ends = daysUntil(s.currentPeriodEnd);
    return s.isTrialActive && typeof ends === 'number' && ends >= 0 && ends <= ENDING_SOON_DAYS;
  });

  const isBaseAfterTrial = computed<boolean>(() => {
    const s = billingStatus.value;
    if (!s) return false;
    return !!s.trialEndsAt && !s.isTrialActive && !s.hasAnySubscriptionHistory && !hasActivePlan.value;
  });

  const isTrialEnded = computed<boolean>(() => {
    const s = billingStatus.value;
    if (!s) return false;
    return !!s.trialEndsAt && !s.isTrialActive && !s.hasAnySubscriptionHistory && !s.hasSeenTrialExpiredHint;
  });

  return { isTrialEnding, isTrialEnded, isBaseAfterTrial };
}

function getCurrentProduct(billingStatus: BillingStatus) {
  const currentBillingStatusProduct = computed(() => {
    const status = billingStatus.value;
    const primaryProduct = status?.products.find((product) => product.productTypeId === status.primaryProductTypeId);
    const latestProduct = status?.products[0];
    return primaryProduct ?? (latestProduct && isPurchasableProduct(latestProduct.product) ? latestProduct : null);
  });

  const currentProductMeta = computed(() => {
    const product = currentBillingStatusProduct.value;
    return product ? getProductMeta(product.product) : undefined;
  });

  const currentBillingInterval = computed<BillingInterval | null>(
    () => billingStatus.value?.currentBillingInterval ?? currentBillingStatusProduct.value?.billingInterval ?? null,
  );

  return { currentBillingStatusProduct, currentProductMeta, currentBillingInterval };
}

/** Subscription state and feature gates subscribe only to billing status. */
export function useBillingState() {
  const { hasTenantOwnerAccess } = usePermissions();
  const { billingStatus, isBillingStatusLoading } = useBillingStatus();
  const canManageBilling = computed(() => !!billingStatus.value?.isBillingActive && hasTenantOwnerAccess.value);
  const plans = getPlanState(billingStatus);
  const { hasActivePlan } = plans;
  const trials = getTrialState(billingStatus, hasActivePlan);
  const { isTrialEnding, isTrialEnded } = trials;
  const subscriptions = getSubscriptionState(billingStatus);
  const { isSubEnding, isSubEnded } = subscriptions;
  const isActiveStatus = computed<boolean>(() => {
    const s = billingStatus.value;
    if (!s) return false;

    if (isTrialEnding.value || isTrialEnded.value || isSubEnding.value || isSubEnded.value) return false;

    return s.isTrialActive || s.hasActivePlan;
  });

  const canCreateBillingOffer = computed<boolean>(() => {
    const status = billingStatus.value;
    return canManageBilling.value && canCreateBillingOfferFromStatus(status);
  });

  const endsAt = computed<string | null>(() => billingStatus.value?.currentPeriodEnd ?? null);
  const endsInDays = computed<number | null>(() => daysUntil(billingStatus.value?.currentPeriodEnd ?? null));

  const isBillingActive = computed(() => !!billingStatus.value?.isBillingActive);

  const showTrialExpiredDialog = computed<boolean>(() => {
    if (!canManageBilling.value) return false;
    const status = billingStatus.value;
    if (!status) return false;

    if (status.hasSeenTrialExpiredHint) return false;
    return isTrialEnded.value && !hasActivePlan.value;
  });

  return {
    ...getFeatureAccess(billingStatus),
    ...getCurrentProduct(billingStatus),
    ...plans,
    ...trials,
    ...subscriptions,
    billingStatus,
    isBillingStatusLoading,
    canManageBilling,
    canCreateBillingOffer,
    isActiveStatus,
    endsAt,
    endsInDays,
    isBillingActive,
    showTrialExpiredDialog,
  };
}
